<script setup lang="ts">
import {
  onActivated,
  onUnmounted,
  onMounted,
  computed,
  watch,
  nextTick,
  ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PerfilOficialSwiper from '@/components/perfil-oficial/Swiper.vue';
import TournamentDisponiveis from '@/components/smartico/apostatudo/items/tournament-cards/TournamentDisponiveis.vue';
import TournamentParticipando from '@/components/smartico/apostatudo/items/tournament-cards/TournamentParticipando.vue';
import TournamentFinalizados from '@/components/smartico/apostatudo/items/tournament-cards/TournamentFinalizados.vue';
import TournamentGridItem from '@/components/smartico/apostatudo/items/tournament-cards/TournamentGridItem.vue';
import TournamentFeatured from '@/components/smartico/apostatudo/items/tournament-cards/TournamentFeatured.vue';

const route = useRoute();
const router = useRouter();
const { isDesktop } = useLayout();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();
const { allTournaments, exclusiveTournaments } = storeToRefs(
  useSmarticoTournaments(),
);

const {
  loadTournaments,
  registerCustomUpdateCallback,
  unregisterCustomUpdateCallback,
} = useSmarticoTournaments();

const { debugLog } = useSmarticoConfigs();

const { gamificationExclusiveTournaments } = storeToRefs(useSmarticoConfigs());

const { isReady, initialLoadComplete, user } = useSmarticoUserReady(undefined, [
  'getTournamentsList',
]);

const selectedTournament = ref<TTournament | null>(null);

const isTournamentsLoading = ref<boolean>(false);
const callbackId = 'tournaments-component-callback';
const isEventsInitialized = ref<boolean>(false);

const ribbonTab = ref<number>(0);

const tabsContainerRef = ref<HTMLElement | null>(null);

const sortName = ref<string>('order_status');
const search = ref<string>('');
const debouncedSearch = ref<string>('');
const isSearchExpanded = ref<boolean>(false);
const searchInputRef = ref<HTMLInputElement | null>(null);
let searchDebounceTimeout: ReturnType<typeof setTimeout> | null = null;

const smarticoPageRef = ref<HTMLElement | null>(null);

function normalizeString(str: string): string {
  return str
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036F]/g, '')
    .toLowerCase();
}

const upcomingTournaments = computed(() =>
  // DISPONÍVEIS = Todos os torneios ativos (não finalizados/cancelados) onde o usuário NÃO está participando
  useToList.value.filter(
    (tournament: TTournament) =>
      !tournament.is_finished &&
      !tournament.is_cancelled &&
      !tournament.is_user_registered,
  ),
);

const inProgressTournaments = computed(() =>
  // PARTICIPANDO = Apenas torneios onde o usuário está registrado E que não estão finalizados/cancelados
  // Se não autenticado, retorna array vazio
  isAuthenticated.value
    ? useToList.value.filter(
        (tournament: TTournament) =>
          tournament.is_user_registered &&
          !tournament.is_finished &&
          !tournament.is_cancelled,
      )
    : [],
);

const finishedTournaments = computed(() =>
  // FINALIZADOS = Torneios terminados ou cancelados
  useToList.value.filter(
    (tournament: TTournament) =>
      tournament.is_finished || tournament.is_cancelled,
  ),
);

const ribbonTabs = computed(() => {
  const allTabs = [
    { name: 'Todos', value: 0 },
    { name: 'Disponíveis', value: 1 },
    { name: 'Participando', value: 2 },
    { name: 'Finalizados', value: 3 },
  ];

  // Antes do load inicial terminar, mantém todas visíveis pra evitar flick
  if (!initialLoadComplete.value) return allTabs;

  return allTabs.filter((tab) => {
    if (tab.value === 0) return true;
    if (tab.value === 1) return upcomingTournaments.value.length > 0;
    if (tab.value === 2) return inProgressTournaments.value.length > 0;
    if (tab.value === 3) return finishedTournaments.value.length > 0;

    return true;
  });
});

// Versões para swipers da aba "TODOS" - ordenadas apenas por priority
const upcomingTournamentsForSwiper = computed(() =>
  [...upcomingTournaments.value].sort(
    (a, b) => (a as any).priority - (b as any).priority,
  ),
);

const inProgressTournamentsForSwiper = computed(() =>
  [...inProgressTournaments.value].sort(
    (a, b) => (a as any).priority - (b as any).priority,
  ),
);

const finishedTournamentsForSwiper = computed(() =>
  [...finishedTournaments.value].sort(
    (a, b) => (a as any).priority - (b as any).priority,
  ),
);

const orderedTournaments = computed(() => {
  let tournamentList: TTournament[] = [];

  if (ribbonTab.value === 1) {
    tournamentList = upcomingTournaments.value;
  } else if (ribbonTab.value === 2) {
    tournamentList = inProgressTournaments.value;
  } else if (ribbonTab.value === 3) {
    tournamentList = finishedTournaments.value;
  } else {
    tournamentList = useToList.value;
  }

  if (ribbonTab.value !== 0 && debouncedSearch.value.length >= 2) {
    const valueToSearch = debouncedSearch.value.toString().toLowerCase();

    tournamentList = tournamentList.filter((tournament: TTournament) =>
      tournament.name.toString().toLowerCase().includes(valueToSearch),
    );
  }

  if (sortName.value === 'order_name_asc') {
    return [...tournamentList].sort((a, b) => a.name.localeCompare(b.name));
  }
  if (sortName.value === 'order_name_desc') {
    return [...tournamentList].sort((a, b) => b.name.localeCompare(a.name));
  }

  const sortedTournaments = [...tournamentList].sort((a, b) => {
    if (a.is_in_progress && !b.is_in_progress) return -1;
    if (!a.is_in_progress && b.is_in_progress) return 1;
    if (a.is_upcoming && !b.is_upcoming) return 1;
    if (!a.is_upcoming && b.is_upcoming) return -1;

    return (a as any).priority - (b as any).priority;
  });

  return sortedTournaments;
});

const hasNoTournamentsToShow = computed(() => {
  if (!initialLoadComplete.value || isTournamentsLoading.value) return false;

  if (ribbonTab.value !== 0) {
    return orderedTournaments.value.length === 0;
  }

  return (
    upcomingTournaments.value.length === 0 &&
    inProgressTournaments.value.length === 0 &&
    finishedTournaments.value.length === 0
  );
});

// whether we're on an exclusive/premium variant of the page
const isExclusivePage = computed(() =>
  Boolean(
    route.query?.exclusive ||
      route.query?.premium ||
      route.query?.telegram ||
      route.query?.whatsapp,
  ),
);

const featuredTournaments = computed(() => {
  const list = isExclusivePage.value
    ? exclusiveTournaments.value
    : allTournaments.value;

  function hasFeaturedFlag(custom_data: any) {
    if (!custom_data) return false;
    if (typeof custom_data === 'string')
      return custom_data.includes('TORNEIO-EM-DESTAQUE');
    if (Array.isArray(custom_data))
      return custom_data.some((v) => String(v).includes('TORNEIO-EM-DESTAQUE'));
    if (typeof custom_data === 'object')
      return Object.values(custom_data).some((v) =>
        String(v).includes('TORNEIO-EM-DESTAQUE'),
      );

    return false;
  }

  return list.filter(
    (t: TTournament) =>
      hasFeaturedFlag(t.custom_data) && (t.is_upcoming || t.is_in_progress),
  );
});

function toggleSearch() {
  isSearchExpanded.value = !isSearchExpanded.value;
  if (isSearchExpanded.value) {
    nextTick(() => {
      searchInputRef.value?.focus();
    });
  } else {
    search.value = '';
    debouncedSearch.value = '';
  }
}

function handleSearchInput() {
  if (searchDebounceTimeout) {
    clearTimeout(searchDebounceTimeout);
  }
  searchDebounceTimeout = setTimeout(() => {
    debouncedSearch.value = search.value;
  }, 400);
}

function clearSearch() {
  search.value = '';
  debouncedSearch.value = '';
  searchInputRef.value?.focus();
}

function closeSearch() {
  isSearchExpanded.value = false;
  search.value = '';
  debouncedSearch.value = '';
}

function scrollActiveTabIntoView() {
  try {
    const container = tabsContainerRef.value;

    if (!container) return;

    const activeButton = container.querySelector(
      '[class*="activeTab"]',
    ) as HTMLElement;

    if (activeButton) {
      const scrollLeft =
        activeButton.offsetLeft -
        container.offsetWidth / 2 +
        activeButton.offsetWidth / 2;

      container.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth',
      });
    }
  } catch {}
}

function scrollEverythingToTopSmooth() {
  try {
    if (
      typeof window !== 'undefined' &&
      typeof window.scrollTo === 'function'
    ) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  } catch {}
  try {
    if (typeof document !== 'undefined') {
      (document.documentElement as any).scrollTop = 0;
      (document.body as any).scrollTop = 0;
    }
  } catch {}
  try {
    let node = smarticoPageRef.value as HTMLElement | null;

    while (node) {
      const style = window.getComputedStyle(node);
      const overflowY = style?.overflowY;

      if (
        (overflowY === 'auto' ||
          overflowY === 'scroll' ||
          overflowY === 'overlay') &&
        node.scrollHeight > node.clientHeight
      ) {
        try {
          if (typeof (node as any).scrollTo === 'function') {
            (node as any).scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            (node as any).scrollTop = 0;
          }
        } catch {}
      }
      node = node.parentElement as HTMLElement | null;
    }
  } catch {}
}

watch(
  () => route.query.tab,
  (newTab) => {
    const normalizedQuery = normalizeString(String(newTab || ''));
    const validTab = ribbonTabs.value.find(
      (tab) =>
        normalizeString(String(tab.value)) === normalizedQuery ||
        normalizeString(tab.name) === normalizedQuery,
    );

    if (validTab && ribbonTab.value !== validTab.value) {
      ribbonTab.value = validTab.value;
    }
    if (!newTab && ribbonTab.value !== 0) {
      ribbonTab.value = 0;
    }
  },
);

watch(ribbonTab, () => {
  nextTick(() => {
    scrollEverythingToTopSmooth();
    scrollActiveTabIntoView();
  });
  search.value = '';
  debouncedSearch.value = '';
  isSearchExpanded.value = false;
});

// Se a tab ativa sumir (categoria ficou vazia após reload), volta pra "Todos"
watch(ribbonTabs, (newTabs) => {
  if (!newTabs.some((tab) => tab.value === ribbonTab.value)) {
    ribbonTab.value = 0;
  }
});

function setTabByQuery(tabValue: number) {
  ribbonTab.value = tabValue;

  const tabObj = ribbonTabs.value.find((t) => t.value === tabValue);
  const tabParam = tabObj
    ? normalizeString(tabObj.name)
    : normalizeString(String(tabValue));

  if (normalizeString(String(route.query.tab || '')) !== tabParam) {
    router.replace({
      query: {
        ...route.query,
        tab: tabParam,
      },
    });
  }
}

function handleTournamentUpdate(updatedTournaments: TTournament[]) {
  if (selectedTournament.value) {
    const sourceList = isExclusivePage.value
      ? updatedTournaments.filter((tournament: TTournament) =>
          gamificationExclusiveTournaments.value.includes(
            String(tournament.tournament_id),
          ),
        )
      : updatedTournaments.filter(
          (tournament: TTournament) =>
            !gamificationExclusiveTournaments.value.includes(
              String(tournament.tournament_id),
            ),
        );

    if (sourceList.length > 0) {
      const updatedTournament = sourceList.find(
        (tournament: TTournament) =>
          tournament.instance_id === selectedTournament.value?.instance_id,
      );

      if (updatedTournament) {
        selectedTournament.value = updatedTournament;
        debugLog('Tournament selected updated');
      }
    }
  }
}

function viewTournament(tournamentId: number) {
  if (!isAuthenticated.value) {
    openLogin();

    return;
  }
  navigateTo(localeLink(`/vip/tournaments/${tournamentId ?? ''}`));
}

function handleBackNavigation() {
  if (typeof window !== 'undefined') {
    try {
      const originUrl = sessionStorage.getItem('casino_origin_url');

      // Clear the origin URL to prevent reuse on subsequent navigations
      sessionStorage.removeItem('casino_origin_url');

      if (originUrl && originUrl.includes('/games/')) {
        // Navigate back to the saved games page URL
        router.push(originUrl);

        return;
      }
    } catch (e) {
      // Ignore storage errors
    }
  }
  // Default to home if no origin URL is saved
  router.push('/');
}

handleTournamentUpdate.id = callbackId;

const useToList = computed(() => {
  return isExclusivePage.value
    ? exclusiveTournaments.value
    : allTournaments.value;
});

async function start() {
  isTournamentsLoading.value = true;
  await loadTournaments();
  isTournamentsLoading.value = false;
  initializeSmarticoEvents();
}

function initializeSmarticoEvents() {
  if (
    import.meta.client &&
    window &&
    window._smartico &&
    !isEventsInitialized.value
  ) {
    window._smartico.on('tournament_update', () => {
      debugLog('Tournament_update event detected - updating data');
      loadTournaments();
    });

    window._smartico.on('tournament_registration', () => {
      debugLog('Tournament_registration event detected - updating data');
      loadTournaments();
    });

    isEventsInitialized.value = true;
  }
}

watch(
  isReady,
  async (ready) => {
    if (ready) {
      debugLog('User and Smartico ready, initializing tournaments...');
      await start();
      initialLoadComplete.value = true;
    }
  },
  { immediate: true },
);

onActivated(() => {
  if (user.value && user.value.id) {
    registerCustomUpdateCallback(handleTournamentUpdate);
  }
});

onMounted(() => {
  const tabQuery = route.query.tab;

  if (tabQuery) {
    const normalizedQuery = normalizeString(String(tabQuery));
    const validTab = ribbonTabs.value.find(
      (tab) =>
        normalizeString(String(tab.value)) === normalizedQuery ||
        normalizeString(tab.name) === normalizedQuery,
    );

    if (validTab && ribbonTab.value !== validTab.value) {
      ribbonTab.value = validTab.value;
    }
  }
});

onUnmounted(() => {
  unregisterCustomUpdateCallback(handleTournamentUpdate);
  if (searchDebounceTimeout) {
    clearTimeout(searchDebounceTimeout);
  }
});
</script>

<template>
  <div
    ref="smarticoPageRef"
    :class="$style.smarticoPageTournaments"
  >
    <BaseScreenHeader
      heading-tag="h1"
      :on-back="handleBackNavigation"
      :title="$t('gamification.profileMenu.tournaments')"
    >
      <template #right>
        <div
          v-if="ribbonTab !== 0"
          :class="[
            $style.searchContainer,
            { [$style.expanded]: isSearchExpanded },
          ]"
        >
          <div :class="$style.searchInputWrapper">
            <span :class="$style.searchIconDesktop">
              <NuxtIcon
                aria-hidden="true"
                name="search"
              />
            </span>
            <input
              ref="searchInputRef"
              v-model="search"
              :class="$style.searchInput"
              placeholder="Pesquisar torneios..."
              type="text"
              @input="handleSearchInput"
              @keyup.escape="closeSearch"
            />
            <button
              v-if="search.length > 0"
              aria-label="Limpar busca"
              :class="$style.clearButton"
              @click="clearSearch"
            >
              <NuxtIcon
                aria-hidden="true"
                name="close"
              />
            </button>
          </div>
          <button
            aria-label="Buscar torneios"
            :class="$style.searchButton"
            @click="toggleSearch"
          >
            <NuxtIcon
              aria-hidden="true"
              :name="isSearchExpanded ? 'arrows/angle_down' : 'search'"
            />
          </button>
        </div>
      </template>
    </BaseScreenHeader>

    <!-- Featured banner component -->
    <TournamentFeatured
      v-if="isDesktop && featuredTournaments.length > 0"
      :tournaments="featuredTournaments"
      @view="viewTournament"
    />

    <div
      ref="tabsContainerRef"
      :class="$style.tabs"
    >
      <BaseFilterTabs
        :model-value="ribbonTab"
        :tabs="ribbonTabs"
        @update:model-value="setTabByQuery($event as number)"
      />
    </div>

    <!-- Skeleton apenas no conteúdo (header e tabs permanecem visíveis) -->
    <template v-if="!initialLoadComplete">
      <!-- Tab "Todos" usa skeleton com seções de swiper -->
      <SmarticoApostatudoSkeletonSwiperSectionSkeleton
        v-if="ribbonTab === 0"
        :card-height="200"
        :card-min-width="240"
        :cards-per-section="4"
        :sections="3"
      />
      <!-- Outras tabs usam skeleton de grid -->
      <SmarticoApostatudoSkeletonTournamentsSkeleton
        v-else
        :count="6"
      />
    </template>

    <template v-else>
      <div
        v-if="ribbonTab !== 0"
        ref="tournamentsContainerRef"
        :class="$style.gridItems"
      >
        <template v-if="orderedTournaments.length > 0">
          <template v-if="ribbonTab === 1">
            <TournamentGridItem
              v-for="tournament in orderedTournaments"
              :key="tournament.instance_id"
              :tournament="tournament"
              variant="disponiveis"
            />
          </template>

          <template v-else-if="ribbonTab === 2">
            <TournamentGridItem
              v-for="tournament in orderedTournaments"
              :key="tournament.instance_id"
              :tournament="tournament"
              variant="participando"
            />
          </template>

          <template v-else-if="ribbonTab === 3">
            <TournamentGridItem
              v-for="tournament in orderedTournaments"
              :key="tournament.instance_id"
              :tournament="tournament"
              variant="finalizados"
            />
          </template>

          <template v-else>
            <SmarticoItemsTournamentV2
              v-for="tournament in orderedTournaments"
              :key="tournament.instance_id"
              :tournament="tournament"
              :tournament-id="tournament.instance_id"
            />
          </template>
        </template>
      </div>

      <!-- Secoes para a aba Todas -->
      <div v-if="ribbonTab === 0">
        <div
          v-if="upcomingTournamentsForSwiper.length > 0"
          :class="$style.tournamentsContainer"
        >
          <PerfilOficialSwiper
            ref="upcomingSwiperRef"
            icon-path="tournament/section/disponiveis"
            see-more-url="/vip/tournaments?tab=disponíveis"
            title="Disponíveis"
          >
            <TournamentDisponiveis
              v-for="tournament in upcomingTournamentsForSwiper"
              :key="tournament.instance_id"
              :tournament="tournament"
            />
          </PerfilOficialSwiper>
        </div>

        <div
          v-if="inProgressTournamentsForSwiper.length > 0"
          class="mt-4"
          :class="$style.tournamentsContainer"
        >
          <PerfilOficialSwiper
            ref="inProgressSwiperRef"
            icon-path="tournament/section/participando"
            see-more-url="/vip/tournaments?tab=participando"
            title="Participando"
          >
            <TournamentParticipando
              v-for="tournament in inProgressTournamentsForSwiper"
              :key="tournament.instance_id"
              :tournament="tournament"
            />
          </PerfilOficialSwiper>
        </div>

        <div
          v-if="finishedTournamentsForSwiper.length > 0"
          class="mt-4"
          :class="$style.tournamentsContainer"
        >
          <PerfilOficialSwiper
            ref="finishedSwiperRef"
            icon-path="tournament/section/finalizados"
            see-more-url="/vip/tournaments?tab=finalizados"
            title="Finalizados"
          >
            <TournamentFinalizados
              v-for="tournament in finishedTournamentsForSwiper"
              :key="tournament.instance_id"
              :tournament="tournament"
            />
          </PerfilOficialSwiper>
        </div>
      </div>

      <!-- Warning centralizado para quando não há torneios -->
      <div
        v-if="hasNoTournamentsToShow"
        class="alert alert-warning"
      >
        <span>
          {{ $t('components.search_bar.no_results') }}
        </span>
      </div>
    </template>
  </div>
</template>

<style module lang="postcss">
.smarticoPageTournaments {
  @apply mb-4 w-full -mt-3 lg:-mt-1;

  .gridItems {
    display: grid;
    /* Mobile: min 150px por card, cresce automaticamente */
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    gap: 10px;
    width: 100%;
    max-width: 100%;
    /* Isola contexto de composição para evitar fragmentos visuais de blur/shadow */
    isolation: isolate;
  }

  /* Alert ocupa linha toda */
  .gridItems > :global(.alert) {
    grid-column: 1 / -1;
  }

  /* Desktop: min 240px por card */
  @media (min-width: 640px) {
    .gridItems {
      grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    }
  }

  .loadingContainer {
    @apply flex items-center justify-center min-h-[200px];

    & > * {
      animation: none !important;
    }
  }

  .featuredNav svg,
  .featuredNav > * {
    display: block; /* evita desalinhamento por baseline de inline elements */
  }

  .gridItems > *:not(:only-child) {
    animation: shuffle-card 0.7s ease-in-out;
    animation-fill-mode: forwards;
    width: 100%;
    min-width: 0;
  }

  .tournamentsContainer {
    animation: shuffle-card 0.7s ease-in-out;
    animation-fill-mode: forwards;
    /* Isola contexto de composição para evitar fragmentos visuais de blur/shadow */
    isolation: isolate;
    contain: layout style paint;
  }

  /* Featured swiper styles (simple fade) */
  /* featured styles moved to TournamentFeatured component */
}

/* Animação apenas para os cards do grid */
@keyframes shuffle-card {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.tabs {
  @apply mt-2 mb-4 border-b border-[#424344] select-none;

  @media (min-width: 1024px) {
    :global(.filter-tabs) {
      justify-content: center;
    }
  }
}

/* Searchbar expansível */
.searchContainer {
  @apply flex items-center relative;
  transition: all 0.3s ease;
}

.searchButton {
  @apply flex items-center justify-center w-9 h-9 rounded-full bg-transparent text-texts/60 hover:text-texts transition-colors;
  z-index: 2;

  svg {
    @apply text-lg;
  }

  /* Esconde o botão no desktop */
  @media (min-width: 1024px) {
    @apply hidden;
  }
}

.searchInputWrapper {
  @apply flex items-center overflow-hidden;
  width: 0;
  opacity: 0;
  transition:
    width 0.3s ease,
    opacity 0.3s ease;

  /* No desktop, sempre visível */
  @media (min-width: 1024px) {
    width: 250px;
    opacity: 1;
  }
}

.expanded .searchInputWrapper {
  width: 190px;
  opacity: 1;
}

.searchIconDesktop {
  @apply flex items-center justify-center absolute left-2 text-black/50;
  z-index: 2;

  svg {
    @apply text-base;
  }
}

.searchInput {
  @apply w-full h-8 pl-8 pr-3 bg-[#fff] border border-[#30364d] rounded-lg text-black text-sm placeholder-black/50;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: #00c9ff;
  }
}

.clearButton {
  @apply absolute right-10 flex items-center justify-center w-5 h-5 text-black/40 hover:text-black transition-colors;

  svg {
    @apply text-xs;
  }

  /* No desktop, ajusta posição pois não há botão de busca */
  @media (min-width: 1024px) {
    @apply right-2;
  }
}

/* Featured transitions moved to TournamentFeatured component */
</style>
